import React from 'react'
import { useAuth } from '../../context/AuthContext'
import { recurringTaskService } from '../../services/recurringTaskService'
import { Button } from '../../components/ui/Button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog'
import type { BlitzReview } from '../../types/recurringTasks'
import type { ApprovalActionProps } from './taskTypeRegistry'

/** The server reads the original attachment and repeats every check when saving.
 * No client-parsed report or customer-name checkbox can bypass the CIDN gate. */
export function BlitzReportApprovalAction({ approval, onComplete, onCancel }: ApprovalActionProps) {
  const { partner } = useAuth()
  const partnerId = partner?.partnerId || ''
  const [review, setReview] = React.useState<BlitzReview | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [confirmed, setConfirmed] = React.useState(false)
  const [enableAutoSync, setEnableAutoSync] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    setReview(null)
    setConfirmed(false)
    setEnableAutoSync(false)
    setLoading(true)
    setError(null)
    recurringTaskService.reviewBlitz(partnerId, approval.id).then(res => {
      if (cancelled) return
      if (!res.success || !res.data) throw new Error(res.error || 'Could not validate this report')
      setReview(res.data)
    }).catch(err => { if (!cancelled) setError(err?.message || 'Could not validate this report') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [partnerId, approval.id])

  const save = async (completedManually: boolean) => {
    if (!review || !confirmed || saving) return
    setSaving(true)
    setError(null)
    try {
      const res = await recurringTaskService.approveBlitz(partnerId, approval.id, {
        reviewToken: review.reviewToken, customerConfirmed: confirmed, enableAutoSync, completedManually,
      })
      if (!res.success) throw new Error(res.error || 'The report could not be approved')
      onComplete()
    } catch (err: any) {
      setError(err?.message || 'The report could not be approved. Reopen the review to refresh its checks.')
      setConfirmed(false)
    } finally { setSaving(false) }
  }

  return (
    <Dialog open onOpenChange={open => { if (!open && !saving) onCancel() }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review Blitz report</DialogTitle>
          <DialogDescription>Verify the customer once. Future reports can sync automatically after the same checks pass.</DialogDescription>
        </DialogHeader>
        {loading && <p role="status" className="text-sm text-slate-600 py-4">Checking the original workbook…</p>}
        {error && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-800">{error}</p>}
        {review && <div className="space-y-4">
          <dl className="grid grid-cols-[auto_1fr] gap-x-5 gap-y-2 rounded-lg bg-slate-50 p-4 text-sm">
            <dt className="text-slate-500">Customer profile</dt><dd className="font-semibold">{review.customerName}</dd>
            <dt className="text-slate-500">Expected CIDN</dt><dd>{review.expectedCidn || 'Not configured'}</dd>
            <dt className="text-slate-500">Workbook CIDN</dt><dd>{review.cidn || 'Unavailable'}</dd>
            <dt className="text-slate-500">Telstra account names</dt><dd>{review.accountNames.join(', ') || 'Unavailable'}</dd>
            <dt className="text-slate-500">Statement period</dt><dd>{review.statementMonth || 'Unavailable'}</dd>
            <dt className="text-slate-500">Bill total</dt><dd>{review.total.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })} · {review.rows} services</dd>
          </dl>
          {review.errors.map(message => <p key={message} role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-800">{message}</p>)}
          {review.warnings.map(message => <p key={message} className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">{message}</p>)}
          {(review.canPublish || review.canCompleteManually) && <>
            <label className="flex items-start gap-3 text-sm text-slate-700">
              <input type="checkbox" checked={confirmed} disabled={saving} onChange={e => setConfirmed(e.target.checked)} className="mt-1" />
              <span>I have verified that these Telstra account names and CIDN belong to {review.customerName}, and the statement period is correct.</span>
            </label>
            {review.autoSyncEnabled ? <p className="text-sm text-emerald-700">Auto-sync is on. Any newly confirmed account names will be remembered for this CIDN.</p> :
              <label className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 text-sm text-slate-700">
                <input type="checkbox" checked={enableAutoSync} disabled={saving} onChange={e => setEnableAutoSync(e.target.checked)} className="mt-1" />
                <span>Automatically approve future Blitz reports for this CIDN. Every report must pass customer, billing, period and duplicate checks. Existing pending reports stay in review.</span>
              </label>}
          </>}
        </div>}
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={onCancel}>Close</Button>
          {review?.canCompleteManually && <Button variant="outline" disabled={!confirmed || saving} onClick={() => save(true)}>
            {saving ? 'Saving…' : 'Mark completed manually'}
          </Button>}
          {review?.canPublish && <Button disabled={!confirmed || saving} onClick={() => save(false)}>
            {saving ? 'Saving…' : 'Approve & save'}
          </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
